// #region imports

// --- Angular
import { Component, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

// --- RxJs
import { BehaviorSubject, Observable, Subscription, forkJoin, switchMap } from 'rxjs';

// --- 3rd Party
import { User as OidcUser } from 'oidc-client';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';

// --- Hs Code
import { environment } from '../../../../environments/environment';
import {
  AlertModel,
  Constants,
  LanguageModel,
  MessengerSecurity,
  NotificationModel,
  NotificationTypes,
  SavedReportTypes,
  SortDirections,
  SurveyStatuses,
  SurveySummaryModel,
  ToastModel,
  UserDetailsModel,
} from '../../../models';
import {
  AppService,
  AppToastService,
  DemographicsBaseService,
  EnvService,
  LanguagesBaseService,
  OidcService,
  ProfileBaseService,
  ReportsBaseService,
  ResponseBaseService,
  SelfRegistrationBaseService,
  SurveyBaseService,
  TermsAndConditionsBaseService,
  UserNotificationBaseService
} from '../../../core/services';
import { TermsAndConditionsModalComponent } from '../../../shared/';

// #endregion

@Component({
  selector: 'hs-dashboard-shell',
  templateUrl: './dashboard-shell.component.html',
  styleUrls: ['./dashboard-shell.component.scss'],
})
export class DashboardShellComponent {
  private _surveyName: string = '';
  private _isInitialized: boolean = false;

  private _surveySubscription: Subscription = Subscription.EMPTY;

  selfSurveys$: Observable<SurveySummaryModel[] | null> = new Observable<SurveySummaryModel[]>();
  selectedSelfSurvey$: Observable<SurveySummaryModel | null> = new Observable<SurveySummaryModel | null>();
  identity$: Observable<OidcUser | null> | undefined;
  isAdding$: Observable<boolean> = new Observable<boolean>();
  isLoading$: Observable<boolean> = new Observable<boolean>();
  isParticipant$: Observable<boolean> | undefined;
  isRespondent$: Observable<boolean> | undefined;
  isSelfRegistrationEnabled$: Observable<boolean> | undefined;
  notifications$: Observable<NotificationModel[]> = new Observable<NotificationModel[]>();
  numberOfNewNotifications$: Observable<number> = new Observable<number>();
  hasNotifications$: Observable<boolean> = new Observable<boolean>();
  userDetails$: Observable<UserDetailsModel | null> = new Observable<UserDetailsModel | null>();
  reportIsDownLoading$: Observable<boolean> = new Observable<boolean>();

  notificationTypes = NotificationTypes;

  errorMessage$: Observable<string> = new Observable<string>();
  surveyNameRequiredMessage$: Observable<string> = new Observable<string>();
  surveyNameMaxLengthMessage$: Observable<string> = new Observable<string>();
  isbnNumberRequiredMessage$: Observable<string> = new Observable<string>();
  isbnNumberMaxLengthMessage$: Observable<string> = new Observable<string>();

  shouldShowTACFormSubscription: Subscription = Subscription.EMPTY;
  isDashboardRoute: boolean = false;
  shouldShowSelfRegisterOptions: boolean = false;
  alert: AlertModel | null = null;

  navigationState$ = new BehaviorSubject<boolean>(false);

  languages$: Observable<LanguageModel[]> = new Observable<LanguageModel[]>();
  selectedLanguage$: Observable<LanguageModel> = new Observable<LanguageModel>();

  constructor(
    private _appService: AppService,
    private _cookieService: CookieService,
    private _demographicsService: DemographicsBaseService,
    private _envService: EnvService,
    private _languageService: LanguagesBaseService,
    private _modalService: NgbModal,
    private _notificationService: UserNotificationBaseService,
    private _oidcService: OidcService,
    private _profileService: ProfileBaseService,
    private _reportService: ReportsBaseService,
    private _responseService: ResponseBaseService,
    private _router: Router,
    private _selfRegistrationService: SelfRegistrationBaseService,
    private _surveyService: SurveyBaseService,
    private _termsAndConditionsService: TermsAndConditionsBaseService,
    private _titleService: Title,
    private _toastService: AppToastService,
    private _translate: TranslateService,
  ) {
  }

  ngOnInit() {
    this.isDashboardRoute = this._router.url.toLowerCase() === '/dashboard';
    this.isSelfRegistrationEnabled$ = this._selfRegistrationService.isSelfRegistrationEnabled$;
    this.userDetails$ = this._profileService.userDetails$;
    this._demographicsService.loadDemographics(true);
    this.selfSurveys$ = this._surveyService.selfSurveys$;
    this.selectedSelfSurvey$ = this._surveyService.selectedSelfSurvey$;
    this.identity$ = this._oidcService.identity$;
    this.isAdding$ = this._surveyService.isAdding$;
    this.isLoading$ = this._appService.isLoading$;
    this.isParticipant$ = this._oidcService.isParticipant$;
    this.isRespondent$ = this._oidcService.isRespondent$;
    this.notifications$ = this._notificationService.notifications$;
    this.numberOfNewNotifications$ =
    this._notificationService.numberOfNewNotifications$;
    this.hasNotifications$ = this._notificationService.hasNotifications$;
    this.reportIsDownLoading$ = this._reportService.reportIsDownloading$;

    this.languages$ = this._languageService.languages$;
    this.selectedLanguage$ = this._languageService.selectedLanguage$;

    this._oidcService.getOidcUser();

    this._notificationService.connect();

    this.shouldShowTACFormSubscription =
      this._termsAndConditionsService.shouldShowTACForm$.subscribe(
        (shouldShow) => {
          if (shouldShow) {
            this._modalService.open(TermsAndConditionsModalComponent, {
              windowClass: 'agreements-modal-window',
              centered: true,
              scrollable: true,
              size: 'lg',
              backdrop: 'static',
            });
          }
        }
      );

    this.surveyNameRequiredMessage$ = this._translate.get('surveyName').pipe(
      switchMap((surveyName: string) =>
        this._translate.get('validationMessageFieldRequired', {
          value: surveyName,
        })
      )
    );

    this.surveyNameMaxLengthMessage$ = this._translate.get('surveyName').pipe(
      switchMap((surveyName: string) =>
        this._translate.get('validationMessageFieldMaxLength', {
          value: surveyName,
          x: Constants.SURVEY_NAME_MAXIMUM_LENGTH,
        })
      )
    );

    this.isbnNumberRequiredMessage$ = this._translate.get('isbnNumber').pipe(
      switchMap((isbnNumber) =>
        this._translate.get('validationMessageFieldRequired', {
          value: isbnNumber,
        })
      )
    );

    this.isbnNumberMaxLengthMessage$ = this._translate.get('isbnNumber').pipe(
      switchMap((isbnNumber) =>
        this._translate.get('validationMessageFieldMaxLength', {
          value: isbnNumber,
          x: Constants.ISBN_NUMBER_MAXIMUM_LENGTH,
        })
      )
    );

    this._surveyService.loadSummaries(Constants.MAXIMUM_NUMBER_OF_SELF_SURVEYS, 1, '', '', SortDirections.descending);

    const isInitialActivation = localStorage.getItem(
      Constants.INITIAL_ACTIVATION_KEY
    );
    if (isInitialActivation && isInitialActivation === 'true') {
      localStorage.removeItem(Constants.INITIAL_ACTIVATION_KEY);
      forkJoin(
        this._translate.get('activationToastHeader'),
        this._translate.get('activationToastBody')
      ).subscribe(([header, body]) => {
        this._toastService.show({
          header,
          body,
          autoHide: true,
          delay: 5000,
          class: 'bg-success text-light',
        } as ToastModel);
      });
    }

    (<any>window).Intercom('update', { hide_default_launcher: false });
    document.body.classList.add('intercom-active');
    this.userDetails$.subscribe((result: UserDetailsModel | null) => {
      if (!result) return;

      if (this._isInitialized) return;

      (<any>window).Intercom('shutdown', ); // Should clear any anonymous sessions

      if (result.messengerSecurity === MessengerSecurity.IdentityVerification) {
        (<any>window).Intercom('boot', {
          app_id: environment.apiConfig.intercomId,
          name: result.firstName + ' ' + result.lastName,
          email: result.emailAddress,
          user_hash: result.intercomUserHash,
          created_at: new Date(),
        });
      } else {
        (<any>window).Intercom('boot', {
          api_base: "https://api-iam.intercom.io",
          app_id: environment.apiConfig.intercomId,
          user_id: result.userId,
          given_name: result.firstName,
          family_name: result.lastName,
          email: result.emailAddress,
          intercom_user_jwt: result.intercomUserHash,
        });
      }

      this._isInitialized = true;
    });

    this._titleService.setTitle('Heartstyles - Participant Dashboard');

  }

  ngOnDestroy() {
    document.body.classList.remove('intercom-active');
    if (this.shouldShowTACFormSubscription != Subscription.EMPTY) {
      this.shouldShowTACFormSubscription.unsubscribe();
      this.shouldShowTACFormSubscription = Subscription.EMPTY;
    }
  }

  onSurveyNameChanged(surveyName: string): void {
    this._surveyName = surveyName;
  }

  onStartContinueButtonClicked(survey: SurveySummaryModel) {
    (<any>window).Intercom('update', { hide_default_launcher: true });

    if (!survey) {
      return;
    }

    if (
      this._isSurveyNameMissing(survey) &&
      this._isSurveyNameChangeForbidden(survey)
    ) {
      return;
    }
  }

  onToggleNavigationClick(isOpen: boolean): void {
    this.navigationState$.next(isOpen);
  }

  onCloseNavigation(): void {
    this.onToggleNavigationClick(false);
  }

  onNotificationClick(): void {}

  openNotificationModal(content: TemplateRef<any>): void {
    this._modalService
      .open(content, { centered: true, scrollable: true })
      .result.then(
        (result) => {},
        (reason) => {}
      );
  }

  onNotificationView(notification: NotificationModel): void {
    if (notification.type === NotificationTypes.reportDownloadReady) {
      this._reportService.getReportPdf(
        notification.identifier,
        SavedReportTypes.pdg
      );
    } else if (
      notification.type === NotificationTypes.trackMyJourneyDownloadReady
    ) {
      this._reportService.getReportPdf(
        notification.identifier,
        SavedReportTypes.tmj
      );
    }
    this._notificationService.view(notification);
  }

  onNotificationRemove(notification: NotificationModel): void {
    this._notificationService.remove(notification);
  }

  onSurveyClick(surveyIdentifier: string): void {
    this._surveyService.selectSelfSurvey(surveyIdentifier);
  }

  onLogout(): void {
    this._appService.setLoading(true);
    this._oidcService.signOutRedirect();
    this._notificationService.signOut();
  }

  onLanguageChanged(newLanguage: LanguageModel): void {
    const translateLanguageAndCulture = newLanguage.cultureCode
      ? `${newLanguage.isoTwoLetterName}-${newLanguage.cultureCode}`
      : `${newLanguage.isoTwoLetterName}`;

    this._cookieService.set(
      '.AspNetCore.Culture',
      `c=${translateLanguageAndCulture}|uic=${translateLanguageAndCulture}`
    );

    this._surveySubscription = this.selectedSelfSurvey$.subscribe(
      (result: SurveySummaryModel | null) => {
        if (!result) this._languageService.updateUserLanguage(newLanguage);
        else this._languageService.updateUserLanguage(newLanguage, result.id);
        if (this._surveySubscription) this._surveySubscription.unsubscribe();
      }
    );

    this._translate.use(translateLanguageAndCulture);
  }

  onShowProfileClick(): void {
    this._router.navigate(['profile']);
  }

  onRegisterClicked(registerDetails: { [id: string]: any }): void {
    if (registerDetails['hasIncompleteSurveys']) {
      this.alert = {
        show: true,
        style: 'danger',
        text: this._translate.instant('surveySelfRegistrationAlertIncompleteSurvey'),
        meta: registerDetails['surveyReference']
      } as AlertModel;
    } else {
      this._selfRegistrationService.openSelfRegistration();
    }
  }

  onAlertDismissed(): void {
    this.alert = null;
  }

  onAlertTextClicked(alertIdentifier: string): void {
    this.alert = null;
    if (alertIdentifier) {
      this._responseService.clear();
      this._router.navigate(['/surveys', { id: alertIdentifier }]);
    }
    console.log(alertIdentifier);
  }

  private _isSurveyNameMissing(survey: SurveySummaryModel): boolean {
    return !survey.name || survey.name.length === 0;
  }

  private _isSurveyNameChangeForbidden(survey: SurveySummaryModel): boolean {
    return (
      survey.status > SurveyStatuses.notStarted ||
      !this._surveyName ||
      this._surveyName.length === 0
    );
  }
}
